import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { format } from 'date-fns';
import { dropTask, restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, not, or } from 'ember-truth-helpers';
import CloseButton from 'garaje/components/close-button';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type { ReUploadConfig, ReuploadPolicy } from 'garaje/models/custom-user-document-template';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import type StateService from 'garaje/services/state';
import buildReUploadConfig from 'garaje/utils/build-reupload-config';

interface ManageDocumentTypesModalSignature {
  Args: {
    onClose: () => void;
    onTemplatesChanged?: () => void;
  };
}

export default class ManageDocumentTypesModal extends Component<ManageDocumentTypesModalSignature> {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;

  @tracked templates: CustomUserDocumentTemplateModel[] = [];
  @tracked isAddingNew = false;
  @tracked newTitle = '';
  @tracked newDescription = '';
  @tracked newParsingEnabled = false;
  @tracked newReuploadPolicy: ReuploadPolicy = 'always';
  @tracked newReuploadDays = '365';
  @tracked newReuploadDate: Date | null = null;
  @tracked newShowDatePicker = false;
  @tracked templatePendingDelete: CustomUserDocumentTemplateModel | null = null;
  @tracked showUnsavedChangesModal = false;
  @tracked pendingSwitchTemplate: CustomUserDocumentTemplateModel | null = null;

  // Edit state — only title and description are editable at the template level.
  // Re-upload policy and auto-extract are per-flow settings managed through the
  // add/edit document modal (via the document-upload-page adapter endpoints).
  @tracked editingTemplateId: string | null = null;
  @tracked editTitle = '';
  @tracked editDescription = '';
  @tracked editParsingEnabled = false;
  @tracked editReuploadPolicy: ReuploadPolicy = 'always';
  @tracked editReuploadDays = '365';
  @tracked editReuploadDate: Date | null = null;
  @tracked showDatePicker = false;

  get hasUnsavedChanges(): boolean {
    if (!this.editingTemplateId) return false;
    const template = this.templates.find((t) => t.id === this.editingTemplateId);
    if (!template) return false;
    if (template.title !== this.editTitle.trim()) return true;
    if ((template.description ?? '') !== this.editDescription.trim()) return true;
    if (template.allowLlmParsing !== this.editParsingEnabled) return true;
    const currentPolicy = template.reUploadConfig?.policy ?? 'never';
    if (currentPolicy !== this.editReuploadPolicy) return true;
    if (this.editReuploadPolicy === 'after_days') {
      const currentDays = template.reUploadConfig?.days ? String(template.reUploadConfig.days) : '365';
      if (currentDays !== this.editReuploadDays) return true;
    }
    if (this.editReuploadPolicy === 'on_date_yearly') {
      const storedMonth = template.reUploadConfig?.month;
      const storedDay = template.reUploadConfig?.day;
      const editMonth = this.editReuploadDate ? this.editReuploadDate.getMonth() + 1 : undefined;
      const editDay = this.editReuploadDate ? this.editReuploadDate.getDate() : undefined;
      if (storedMonth !== editMonth || storedDay !== editDay) return true;
    }
    return false;
  }

  get isShowingDeleteConfirmation(): boolean {
    return !!this.templatePendingDelete;
  }

  constructor(owner: unknown, args: ManageDocumentTypesModalSignature['Args']) {
    super(owner, args);
    void this.loadTemplates.perform();
  }

  loadTemplates = restartableTask(async () => {
    if (!this.state.currentCompany?.id) return;
    try {
      const results = await this.store.query('custom-user-document-template', {});
      this.templates = results.slice();
    } catch (e) {
      this.logger.warn('[ManageModal] Failed to load templates:', e as Error);
    }
  });

  // ── Add new ──────────────────────────────────────────────────────────

  showAddForm = (): void => {
    this.editingTemplateId = null;
    this.isAddingNew = true;
    this.newTitle = '';
    this.newDescription = '';
    this.newParsingEnabled = false;
    this.newReuploadPolicy = 'always';
    this.newReuploadDays = '365';
    this.newReuploadDate = null;
    this.newShowDatePicker = false;
  };

  cancelAdd = (): void => {
    this.isAddingNew = false;
    this.newTitle = '';
    this.newDescription = '';
  };

  updateNewParsingEnabled = (checked: boolean): void => {
    this.newParsingEnabled = checked;
  };

  updateNewReuploadPolicy = (evt: Event): void => {
    this.newReuploadPolicy = (evt.target as HTMLInputElement).value as ReuploadPolicy;
  };

  updateNewReuploadDays = (value: string): void => {
    this.newReuploadDays = value.replace(/\D/g, '');
  };

  toggleNewDatePicker = (): void => {
    this.newShowDatePicker = !this.newShowDatePicker;
  };

  selectNewReuploadDate = (date: Date): void => {
    this.newReuploadDate = date;
    this.newShowDatePicker = false;
  };

  get newReuploadDateDisplay(): string {
    if (!this.newReuploadDate) return 'Select a date';
    return format(this.newReuploadDate, 'MMM d, yyyy');
  }

  getNewReUploadConfig(): ReUploadConfig {
    return buildReUploadConfig(this.newReuploadPolicy, this.newReuploadDays, this.newReuploadDate);
  }

  updateNewTitle = (value: string): void => {
    this.newTitle = value;
  };

  updateNewDescription = (value: string): void => {
    this.newDescription = value;
  };

  get canSaveNew(): boolean {
    if (this.newTitle.trim().length === 0) return false;
    if (this.newReuploadPolicy === 'on_date_yearly' && !this.newReuploadDate) return false;
    if (this.newReuploadPolicy === 'after_days' && (!this.newReuploadDays || parseInt(this.newReuploadDays, 10) <= 0))
      return false;
    return true;
  }

  saveNewTemplate = dropTask(async () => {
    let template: CustomUserDocumentTemplateModel | null = null;
    try {
      template = this.store.createRecord('custom-user-document-template', {
        title: this.newTitle.trim(),
        description: this.newDescription.trim() || undefined,
        allowLlmParsing: this.newParsingEnabled,
        reUploadConfig: this.getNewReUploadConfig(),
        company: this.state.currentCompany,
      });
      await template.save();
      this.templates = [...this.templates, template];
      this.isAddingNew = false;
      this.newTitle = '';
      this.newDescription = '';
      this.flashMessages.showAndHideFlash('success', 'Document type created.');
      this.args.onTemplatesChanged?.();
    } catch (e) {
      if (template) {
        template.rollbackAttributes();
        this.store.unloadRecord(template);
      }
      this.logger.error('[ManageModal] Failed to create template', e as Error);
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    }
  });

  // ── Edit ─────────────────────────────────────────────────────────────

  startEdit = (template: CustomUserDocumentTemplateModel): void => {
    if (this.hasUnsavedChanges) {
      this.pendingSwitchTemplate = template;
      this.showUnsavedChangesModal = true;
      return;
    }
    this.applyEditState(template);
  };

  confirmDiscardUnsavedChanges = (): void => {
    const template = this.pendingSwitchTemplate;
    this.showUnsavedChangesModal = false;
    this.pendingSwitchTemplate = null;
    if (template) {
      this.applyEditState(template);
    }
  };

  cancelDiscardUnsavedChanges = (): void => {
    this.showUnsavedChangesModal = false;
    this.pendingSwitchTemplate = null;
  };

  applyEditState = (template: CustomUserDocumentTemplateModel): void => {
    this.isAddingNew = false;
    this.editingTemplateId = template.id;
    this.editTitle = template.title;
    this.editDescription = template.description ?? '';
    this.editParsingEnabled = template.allowLlmParsing;
    this.showDatePicker = false;
    if (template.reUploadConfig) {
      this.editReuploadPolicy = template.reUploadConfig.policy;
      if (template.reUploadConfig.policy === 'after_days' && template.reUploadConfig.days) {
        this.editReuploadDays = String(template.reUploadConfig.days);
      }
      if (
        template.reUploadConfig.policy === 'on_date_yearly' &&
        template.reUploadConfig.month &&
        template.reUploadConfig.day
      ) {
        const now = new Date();
        this.editReuploadDate = new Date(
          now.getFullYear(),
          template.reUploadConfig.month - 1,
          template.reUploadConfig.day,
        );
      }
    } else {
      this.editReuploadPolicy = 'always';
      this.editReuploadDays = '365';
      this.editReuploadDate = null;
    }
  };

  cancelEdit = (): void => {
    this.editingTemplateId = null;
  };

  updateEditTitle = (value: string): void => {
    this.editTitle = value;
  };

  updateEditDescription = (value: string): void => {
    this.editDescription = value;
  };

  updateEditParsingEnabled = (checked: boolean): void => {
    this.editParsingEnabled = checked;
  };

  updateEditReuploadPolicy = (evt: Event): void => {
    this.editReuploadPolicy = (evt.target as HTMLInputElement).value as ReuploadPolicy;
  };

  updateEditReuploadDays = (value: string): void => {
    this.editReuploadDays = value.replace(/\D/g, '');
  };

  toggleDatePicker = (): void => {
    this.showDatePicker = !this.showDatePicker;
  };

  selectEditReuploadDate = (date: Date): void => {
    this.editReuploadDate = date;
    this.showDatePicker = false;
  };

  get editReuploadDateDisplay(): string {
    if (!this.editReuploadDate) return 'Select a date';
    return format(this.editReuploadDate, 'MMM d, yyyy');
  }

  getEditReUploadConfig(): ReUploadConfig {
    return buildReUploadConfig(this.editReuploadPolicy, this.editReuploadDays, this.editReuploadDate);
  }

  get canSaveEdit(): boolean {
    if (this.editTitle.trim().length === 0) return false;
    if (this.editReuploadPolicy === 'on_date_yearly' && !this.editReuploadDate) return false;
    if (
      this.editReuploadPolicy === 'after_days' &&
      (!this.editReuploadDays || parseInt(this.editReuploadDays, 10) <= 0)
    )
      return false;
    return true;
  }

  saveEdit = dropTask(async () => {
    const template = this.templates.find((t) => t.id === this.editingTemplateId);
    if (!template) return;

    try {
      template.title = this.editTitle.trim();
      template.description = this.editDescription.trim() || undefined;
      template.allowLlmParsing = this.editParsingEnabled;
      template.reUploadConfig = this.getEditReUploadConfig();
      await template.save();
      this.templates = [...this.templates];
      this.editingTemplateId = null;
      this.flashMessages.showAndHideFlash('success', 'Document type updated.');
      this.args.onTemplatesChanged?.();
    } catch (e) {
      template.rollbackAttributes();
      this.logger.error('[ManageModal] Failed to save template', e as Error);
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    }
  });

  // ── Delete ───────────────────────────────────────────────────────────

  confirmDeleteTemplate = (template: CustomUserDocumentTemplateModel): void => {
    this.templatePendingDelete = template;
  };

  cancelDelete = (): void => {
    this.templatePendingDelete = null;
  };

  deleteTemplate = dropTask(async () => {
    const template = this.templatePendingDelete;
    if (!template) return;

    try {
      const id = template.id;
      await template.destroyRecord();
      this.templates = this.templates.filter((t) => t.id !== id);
      if (this.editingTemplateId === id) {
        this.editingTemplateId = null;
      }
      this.flashMessages.showAndHideFlash('success', 'Document type deleted.');
      this.args.onTemplatesChanged?.();
    } catch (e) {
      this.logger.error('[ManageModal] Failed to delete template', e as Error);
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    } finally {
      this.templatePendingDelete = null;
    }
  });

  <template>
    <ModalDialog
      @clickOutsideToClose={{false}}
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @onClose={{@onClose}}
    >
      <div>
        <div
          class="relative"
          style="width: auto; min-width: 480px; max-width: 600px; max-height: 80vh; display: flex; flex-direction: column"
          data-test-manage-document-types-modal
        >

          {{! Header }}
          <div class="flex items-center justify-between px-6 pt-6 pb-0">
            <div class="flex flex-col gap-1">
              <h2 class="text-2xl font-semibold text-carbon-70 m-0">Manage document types</h2>
              <p class="text-base text-carbon-60 m-0">Manage document types used across your company.</p>
            </div>
            <CloseButton
              class="!relative !top-auto !right-auto border-0 hover:bg-carbon-5 rounded"
              @onClick={{@onClose}}
            />
          </div>

          {{! Body — scrollable when list grows }}
          <div class="px-6 py-4 space-y-3 overflow-y-auto flex-1 min-h-0">
            {{#if this.loadTemplates.isRunning}}
              <p class="text-sm text-carbon-50 text-center py-4">Loading document types…</p>
            {{else}}

              {{! Template rows }}
              {{#each this.templates as |template|}}
                {{#if (eq this.editingTemplateId template.id)}}
                  {{! ── Expanded edit card ── }}
                  {{! template-lint-disable use-ui-card }}
                  <div
                    class="p-6 bg-carbon-5 border border-carbon-10 rounded-md space-y-4"
                    data-test-manage-template-edit
                    data-test-id={{template.id}}
                  >
                    {{! Header row — title only, no duplicate delete icon }}
                    <span class="text-base font-medium text-carbon-70">{{template.title}}</span>

                    {{! Name field }}
                    <div>
                      <label
                        for="edit-title-{{template.id}}"
                        class="block text-sm font-medium text-carbon-50 leading-none mb-1"
                      >
                        Name
                        <span class="inline-block w-1 h-1 rounded-full bg-red-40 ml-0.5 mb-1"></span>
                      </label>
                      <input
                        id="edit-title-{{template.id}}"
                        type="text"
                        class="input w-full !mb-0"
                        value={{this.editTitle}}
                        {{on "input" (pick "target.value" this.updateEditTitle)}}
                        data-test-edit-title
                      />
                    </div>

                    <p class="text-sm text-papaya-50 !mt-1 mb-0">This feature is not intended for Identity document use</p>

                    {{! Description }}
                    <div>
                      <label
                        for="edit-desc-{{template.id}}"
                        class="block text-sm font-medium text-carbon-50 leading-none mb-1"
                      >
                        Description
                      </label>
                      <textarea
                        id="edit-desc-{{template.id}}"
                        class="input w-full resize-none !mb-0"
                        placeholder="Instructions for the visitor"
                        rows="3"
                        {{on "input" (pick "target.value" this.updateEditDescription)}}
                        data-test-edit-description
                      >{{this.editDescription}}</textarea>
                    </div>

                    {{! Auto-extract toggle }}
                    <div class="flex items-center gap-2 mt-5 mb-[26px]">
                      <input
                        type="checkbox"
                        id="edit-parsing-{{template.id}}"
                        class="custom-checkbox shrink-0"
                        checked={{this.editParsingEnabled}}
                        {{on "change" (pick "target.checked" this.updateEditParsingEnabled)}}
                        data-test-edit-parsing-enabled
                      />
                      <label
                        for="edit-parsing-{{template.id}}"
                        class="text-sm text-carbon-50 cursor-pointer select-none"
                      >
                        Auto-extract expiration dates
                      </label>
                    </div>

                    {{! Re-upload policy }}
                    {{! template-lint-disable use-ui-card }}
                    <div class="p-4 bg-white border border-carbon-20 rounded-lg" data-test-edit-reupload-policy>
                      <h4 class="text-base font-medium text-carbon-70 m-0 mb-4">Expiration & re-upload rules</h4>
                      <div class="flex flex-col gap-2">
                        <label class="flex items-center h-7 gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="edit-reupload-policy-{{template.id}}"
                            value="always"
                            checked={{eq this.editReuploadPolicy "always"}}
                            {{on "change" this.updateEditReuploadPolicy}}
                            class="custom-radio shrink-0"
                            data-test-edit-policy-always
                          />
                          <span class="text-sm font-medium text-carbon-50">Always require re-upload</span>
                        </label>
                        <label class="flex items-center h-7 gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="edit-reupload-policy-{{template.id}}"
                            value="never"
                            checked={{eq this.editReuploadPolicy "never"}}
                            {{on "change" this.updateEditReuploadPolicy}}
                            class="custom-radio shrink-0"
                            data-test-edit-policy-never
                          />
                          <span class="text-sm font-medium text-carbon-50">Never require re-upload</span>
                        </label>
                        <label class="flex items-center h-7 gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="edit-reupload-policy-{{template.id}}"
                            value="on_expiration"
                            checked={{eq this.editReuploadPolicy "on_expiration"}}
                            {{on "change" this.updateEditReuploadPolicy}}
                            class="custom-radio shrink-0"
                            data-test-edit-policy-on-expiration
                          />
                          <span class="text-sm font-medium text-carbon-50">Sync to document's expiration date</span>
                        </label>
                        <div class="flex items-center h-7 gap-2">
                          <label class="flex items-center gap-2 cursor-pointer shrink-0">
                            <input
                              type="radio"
                              name="edit-reupload-policy-{{template.id}}"
                              value="after_days"
                              checked={{eq this.editReuploadPolicy "after_days"}}
                              {{on "change" this.updateEditReuploadPolicy}}
                              class="custom-radio shrink-0"
                              data-test-edit-policy-after-days
                            />
                            <span class="text-sm font-medium text-carbon-50">Set expiration duration</span>
                          </label>
                          {{#if (eq this.editReuploadPolicy "after_days")}}
                            <input
                              type="number"
                              min="1"
                              max="9999"
                              step="1"
                              class="bg-white border border-carbon-10 text-base text-carbon-70 [appearance:textfield] [&amp;::-webkit-outer-spin-button]:appearance-none [&amp;::-webkit-inner-spin-button]:appearance-none"
                              style="width: 64px; height: 28px; padding: 0 8px; margin: 0; line-height: 2; border-radius: 6px; display: inline-block;"
                              value={{this.editReuploadDays}}
                              {{on "input" (pick "target.value" this.updateEditReuploadDays)}}
                              data-test-edit-policy-days-input
                            />
                            <span class="text-sm text-carbon-70">days</span>
                          {{/if}}
                        </div>
                        <div class="flex items-center h-7 gap-2">
                          <label class="flex items-center gap-2 cursor-pointer shrink-0">
                            <input
                              type="radio"
                              name="edit-reupload-policy-{{template.id}}"
                              value="on_date_yearly"
                              checked={{eq this.editReuploadPolicy "on_date_yearly"}}
                              {{on "change" this.updateEditReuploadPolicy}}
                              class="custom-radio shrink-0"
                              data-test-edit-policy-on-date-yearly
                            />
                            <span class="text-sm font-medium text-carbon-50">Set a fixed expiration date</span>
                          </label>
                          {{#if (eq this.editReuploadPolicy "on_date_yearly")}}
                            <div class="relative">
                              <button
                                type="button"
                                class="flex items-center gap-2 border rounded-md bg-white text-base cursor-pointer transition-colors duration-150 hover:border-carbon-40 whitespace-nowrap
                                  {{if this.showDatePicker 'border-gem-50' 'border-carbon-10'}}"
                                style="padding: 2px 8px; line-height: 160%; border-radius: 6px;"
                                {{on "click" this.toggleDatePicker}}
                                data-test-edit-policy-date-trigger
                              >
                                <span
                                  class="{{unless this.editReuploadDate 'text-carbon-40'}} text-carbon-70"
                                >{{this.editReuploadDateDisplay}}</span>
                                {{svgJar "calendar-16" width="16" height="16" class="shrink-0 text-carbon-70"}}
                              </button>
                              {{#if this.showDatePicker}}
                                {{! template-lint-disable use-ui-card }}
                                <div
                                  class="p-3 bg-white border border-carbon-20 rounded-lg"
                                  style="position: absolute; bottom: calc(100% + 8px); right: 0; box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.10); z-index: 2147483647;"
                                  data-test-edit-policy-date-picker
                                >
                                  <EnvoyOneUpCalendar
                                    @selectedDate={{this.editReuploadDate}}
                                    @onSelectDate={{this.selectEditReuploadDate}}
                                  />
                                </div>
                              {{/if}}
                            </div>
                          {{/if}}
                        </div>
                      </div>
                    </div>

                    {{! Action buttons }}
                    <div class="flex items-center justify-between pt-2">
                      <button
                        type="button"
                        class="bg-white border border-carbon-40 rounded-md px-3 py-1.5 text-sm font-medium text-carbon-70 hover:bg-carbon-5 transition-colors flex items-center gap-1"
                        {{on "click" (fn this.confirmDeleteTemplate template)}}
                        data-test-edit-delete-button
                      >
                        <span class="icon-trash" style="width:14px;height:14px"></span>
                        Delete
                      </button>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="bg-white border border-carbon-40 rounded-md px-3 py-1.5 text-sm font-medium text-carbon-70 hover:bg-carbon-5 transition-colors"
                          disabled={{this.saveEdit.isRunning}}
                          {{on "click" this.cancelEdit}}
                          data-test-edit-cancel
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="btn btn--small btn--brand"
                          disabled={{or (not this.hasUnsavedChanges) this.saveEdit.isRunning}}
                          {{on "click" (perform this.saveEdit)}}
                          data-test-edit-save
                        >
                          {{if this.saveEdit.isRunning "Saving…" "Save"}}
                        </button>
                      </div>
                    </div>
                  </div>
                {{else}}
                  {{! ── Collapsed row ── }}
                  {{! template-lint-disable use-ui-card }}
                  <div
                    class="flex items-center justify-between p-6 bg-white border border-carbon-10 rounded-md"
                    data-test-manage-template-row
                    data-test-id={{template.id}}
                  >
                    <span class="text-base font-medium text-carbon-70 truncate">{{template.title}}</span>
                    <div class="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        class="p-1 text-carbon-70"
                        aria-label="Edit document type"
                        {{on "click" (fn this.startEdit template)}}
                        data-test-edit-template
                      >
                        {{svgJar "pencil-edit-16" width="16" height="16"}}
                      </button>
                      <button
                        type="button"
                        class="p-1 text-carbon-40 hover:text-carbon-70 transition-colors"
                        aria-label="Delete document type"
                        {{on "click" (fn this.confirmDeleteTemplate template)}}
                        data-test-delete-template
                      >
                        <span class="icon-trash text-base"></span>
                      </button>
                    </div>
                  </div>
                {{/if}}
              {{/each}}

              {{! template-lint-disable no-negated-condition }}
              {{#if (not this.templates.length)}}
                {{#unless this.isAddingNew}}
                  <p class="text-sm text-carbon-50 text-center py-4" data-test-empty-state>
                    No document types yet. Add one to get started.
                  </p>
                {{/unless}}
              {{/if}}

              {{! Add new form }}
              {{#if this.isAddingNew}}
                {{! template-lint-disable use-ui-card }}
                <div class="p-6 bg-carbon-5 border border-carbon-10 rounded-md space-y-4" data-test-add-new-form>
                  <div>
                    <label for="manage-new-title" class="block text-sm font-medium text-carbon-50 leading-none mb-1">
                      Name
                      <span class="inline-block w-1 h-1 rounded-full bg-red-40 ml-0.5 mb-1"></span>
                    </label>
                    <input
                      id="manage-new-title"
                      type="text"
                      class="input w-full !mb-0"
                      placeholder="E.g. Certificate of Insurance"
                      value={{this.newTitle}}
                      {{on "input" (pick "target.value" this.updateNewTitle)}}
                      data-test-new-template-title
                    />
                  </div>

                  <p class="text-sm text-papaya-50 !mt-1 mb-0">This feature is not intended for Identity document use</p>

                  <div>
                    <label
                      for="manage-new-description"
                      class="block text-sm font-medium text-carbon-50 leading-none mb-1"
                    >
                      Description
                    </label>
                    <textarea
                      id="manage-new-description"
                      class="input w-full resize-none !mb-0"
                      placeholder="Instructions for the visitor"
                      rows="3"
                      {{on "input" (pick "target.value" this.updateNewDescription)}}
                      data-test-new-template-description
                    >{{this.newDescription}}</textarea>
                  </div>

                  {{! Auto-extract toggle }}
                  <div class="flex items-center gap-2 mt-5 mb-[26px]">
                    <input
                      type="checkbox"
                      id="new-parsing-enabled"
                      class="custom-checkbox shrink-0"
                      checked={{this.newParsingEnabled}}
                      {{on "change" (pick "target.checked" this.updateNewParsingEnabled)}}
                      data-test-new-parsing-enabled
                    />
                    <label for="new-parsing-enabled" class="text-sm text-carbon-50 cursor-pointer select-none">
                      Auto-extract expiration dates
                    </label>
                  </div>

                  {{! Re-upload policy }}
                  {{! template-lint-disable use-ui-card }}
                  <div class="p-4 bg-white border border-carbon-20 rounded-lg" data-test-new-reupload-policy>
                    <h4 class="text-base font-medium text-carbon-70 m-0 mb-4">Expiration & re-upload rules</h4>
                    <div class="flex flex-col gap-2">
                      <label class="flex items-center h-7 gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="new-reupload-policy"
                          value="always"
                          checked={{eq this.newReuploadPolicy "always"}}
                          {{on "change" this.updateNewReuploadPolicy}}
                          class="custom-radio shrink-0"
                          data-test-new-policy-always
                        />
                        <span class="text-sm font-medium text-carbon-50">Always require re-upload</span>
                      </label>
                      <label class="flex items-center h-7 gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="new-reupload-policy"
                          value="never"
                          checked={{eq this.newReuploadPolicy "never"}}
                          {{on "change" this.updateNewReuploadPolicy}}
                          class="custom-radio shrink-0"
                          data-test-new-policy-never
                        />
                        <span class="text-sm font-medium text-carbon-50">Never require re-upload</span>
                      </label>
                      <label class="flex items-center h-7 gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="new-reupload-policy"
                          value="on_expiration"
                          checked={{eq this.newReuploadPolicy "on_expiration"}}
                          {{on "change" this.updateNewReuploadPolicy}}
                          class="custom-radio shrink-0"
                          data-test-new-policy-on-expiration
                        />
                        <span class="text-sm font-medium text-carbon-50">Sync to document's expiration date</span>
                      </label>
                      <div class="flex items-center h-7 gap-2">
                        <label class="flex items-center gap-2 cursor-pointer shrink-0">
                          <input
                            type="radio"
                            name="new-reupload-policy"
                            value="after_days"
                            checked={{eq this.newReuploadPolicy "after_days"}}
                            {{on "change" this.updateNewReuploadPolicy}}
                            class="custom-radio shrink-0"
                            data-test-new-policy-after-days
                          />
                          <span class="text-sm font-medium text-carbon-50">Set expiration duration</span>
                        </label>
                        {{#if (eq this.newReuploadPolicy "after_days")}}
                          <input
                            type="number"
                            min="1"
                            max="9999"
                            step="1"
                            class="bg-white border border-carbon-10 text-base text-carbon-70 [appearance:textfield] [&amp;::-webkit-outer-spin-button]:appearance-none [&amp;::-webkit-inner-spin-button]:appearance-none"
                            style="width: 64px; height: 28px; padding: 0 8px; margin: 0; line-height: 2; border-radius: 6px; display: inline-block;"
                            value={{this.newReuploadDays}}
                            {{on "input" (pick "target.value" this.updateNewReuploadDays)}}
                          />
                          <span class="text-sm text-carbon-70">days</span>
                        {{/if}}
                      </div>
                      <div class="flex items-center h-7 gap-2">
                        <label class="flex items-center gap-2 cursor-pointer shrink-0">
                          <input
                            type="radio"
                            name="new-reupload-policy"
                            value="on_date_yearly"
                            checked={{eq this.newReuploadPolicy "on_date_yearly"}}
                            {{on "change" this.updateNewReuploadPolicy}}
                            class="custom-radio shrink-0"
                            data-test-new-policy-on-date-yearly
                          />
                          <span class="text-sm font-medium text-carbon-50">Set a fixed expiration date</span>
                        </label>
                        {{#if (eq this.newReuploadPolicy "on_date_yearly")}}
                          <div class="relative">
                            <button
                              type="button"
                              class="flex items-center gap-2 border rounded-md bg-white text-base cursor-pointer transition-colors duration-150 hover:border-carbon-40 whitespace-nowrap
                                {{if this.newShowDatePicker 'border-gem-50' 'border-carbon-10'}}"
                              style="padding: 2px 8px; line-height: 160%; border-radius: 6px;"
                              {{on "click" this.toggleNewDatePicker}}
                            >
                              <span
                                class="{{unless this.newReuploadDate 'text-carbon-40'}} text-carbon-70"
                              >{{this.newReuploadDateDisplay}}</span>
                              {{svgJar "calendar-16" width="16" height="16" class="shrink-0 text-carbon-70"}}
                            </button>
                            {{#if this.newShowDatePicker}}
                              {{! template-lint-disable use-ui-card }}
                              <div
                                class="p-3 bg-white border border-carbon-20 rounded-lg"
                                style="position: absolute; bottom: calc(100% + 8px); right: 0; box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.10); z-index: 2147483647;"
                              >
                                <EnvoyOneUpCalendar
                                  @selectedDate={{this.newReuploadDate}}
                                  @onSelectDate={{this.selectNewReuploadDate}}
                                />
                              </div>
                            {{/if}}
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  </div>

                  <div class="flex justify-end gap-2 mt-[26px]">
                    <button
                      type="button"
                      class="bg-white border border-carbon-40 rounded-md px-3 py-1.5 text-sm font-medium text-carbon-70 hover:bg-carbon-5 transition-colors"
                      disabled={{this.saveNewTemplate.isRunning}}
                      {{on "click" this.cancelAdd}}
                      data-test-cancel-add
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="btn btn--small btn--brand"
                      disabled={{or (not this.canSaveNew) this.saveNewTemplate.isRunning}}
                      {{on "click" (perform this.saveNewTemplate)}}
                      data-test-save-new-template
                    >
                      {{if this.saveNewTemplate.isRunning "Saving…" "Save"}}
                    </button>
                  </div>
                </div>
              {{/if}}

            {{/if}}
          </div>

          {{! Add button }}
          {{#unless this.isAddingNew}}
            <div class="px-6 pb-6">
              <button
                type="button"
                class="inline-flex items-center gap-2 px-4 py-[11px] border border-carbon-10 rounded-md bg-white text-base font-medium text-carbon-70 hover:bg-carbon-5 transition-colors"
                {{on "click" this.showAddForm}}
                data-test-add-new-type-button
              >
                {{svgJar "circle-plus-carbon" width="16" height="16" class="shrink-0"}}
                Add a new document type
              </button>
            </div>
          {{/unless}}

        </div>
      </div>
    </ModalDialog>

    {{#if this.isShowingDeleteConfirmation}}
      <ConfirmationModal
        @title="Delete document type?"
        @ctaText="Delete"
        @handleTaskAction={{this.deleteTemplate}}
        @onClose={{this.cancelDelete}}
        @closeButtonClass="btn--outlineFill"
      >
        <p class="mt-4 mb-0 text-base text-carbon-60">
          This will permanently delete
          <strong class="text-carbon-70">{{this.templatePendingDelete.title}}</strong>. It will be removed from all
          flows that use it.
        </p>
      </ConfirmationModal>
    {{/if}}

    {{#if this.showUnsavedChangesModal}}
      <ConfirmationModal
        @title="Discard unsaved changes?"
        @ctaText="Discard"
        @handleTaskAction={{this.confirmDiscardUnsavedChanges}}
        @onClose={{this.cancelDiscardUnsavedChanges}}
        @closeButtonClass="btn--outlineFill"
        data-test-unsaved-changes-modal
      >
        <p>You have unsaved changes to the current template. Discard and switch?</p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
